/**
 * Keeps the app-wide date range and the address bar in step (PR 12, C10).
 * Renders nothing; mounted once in the shell next to the header control.
 *
 *   URL to store: a location the sync did not write itself (a pasted link, a
 *   link from another page) that names a range is adopted for this visit. A
 *   plain `/products` link names none and changes nothing.
 *   Store to URL: whenever the range or comparison changes while an opted-in
 *   page is on screen, the range params are written into the URL (replace, no
 *   history entry), so the bottom tab remembers it and a copied link carries
 *   it. A location the sync wrote is never adopted back: the store already
 *   holds the latest pick, one range for the whole app.
 *
 * On a page that has not opted in (`active` false) nothing is read or written.
 * The pure rules live in lib/dateRangeUrl.ts.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getAppDateRangeStore, useAppDateRangeControl } from '../lib/dateRangeContext'
import {
  loadWritten, mergeRangeIntoSearch, rangeFromParams, rangeToParams, sameRange, saveWritten, shouldAdoptFromUrl,
} from '../lib/dateRangeUrl'

/** Remembered written locations; old ones fall off so the set stays small. */
const MAX_WRITTEN = 200

function safeStorage(): Storage | null {
  try { return window.localStorage } catch { return null }
}

export default function AppDateRangeUrlSync() {
  const location = useLocation()
  const navigate = useNavigate()
  const { range, compare } = useAppDateRangeControl()
  // The registry object changes on every register / unregister, so the effect below re-runs when the page on
  // screen changes - and reads the registry as it is NOW (see the guard), not as it was at render.
  const registry = useSyncExternalStore(
    getAppDateRangeStore().subscribe, getAppDateRangeStore().getRegistry, getAppDateRangeStore().getRegistry)
  // Kept in storage too: a bottom tab restored after a reload comes back to a URL this sync wrote.
  const written = useRef<Set<string> | null>(null)
  if (written.current === null) {
    written.current = loadWritten(typeof window === 'undefined' ? null : safeStorage())
  }
  const handled = useRef<string | null>(null)

  useEffect(() => {
    const store = getAppDateRangeStore()
    // Re-read, don't trust the render-time value: leaving an opted-in page for one that has not opted in (the row
    // menu's History link) renders this once with the old page's `active`, and its cleanup has run by the time
    // this effect does. Acting on the stale value wrote ?from&to&preset onto /history, which reads them as its
    // own When filter.
    if (store.getRegistry().count === 0) return
    const key = `${location.pathname}${location.search}`

    // 1. URL to store, once per location.
    if (handled.current !== key) {
      handled.current = key
      if (shouldAdoptFromUrl(key, written.current as Set<string>)) {
        const named = rangeFromParams(new URLSearchParams(location.search))
        if (named) {
          const core = store.getCore()
          if (!sameRange(named.range, core.range)) store.setRange(named.range)
          if (named.compare && JSON.stringify(named.compare) !== JSON.stringify(core.compare)) {
            store.setCompare(named.compare)
          }
        }
      }
    }

    // 2. Store to URL, from the store's current value (adopting above may
    //    have just changed it).
    const core = store.getCore()
    const merged = mergeRangeIntoSearch(location.search, rangeToParams(core))
    if (merged !== location.search) {
      const set = written.current as Set<string>
      set.add(`${location.pathname}${merged}`)
      if (set.size > MAX_WRITTEN) set.delete(set.values().next().value as string)
      saveWritten(safeStorage(), set)
      handled.current = `${location.pathname}${merged}`
      navigate({ pathname: location.pathname, search: merged, hash: location.hash }, { replace: true })
    }
  }, [registry, range, compare, location.pathname, location.search, location.hash, navigate])

  return null
}
